import { useMemo, useState, type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  CalendarClock,
  PackageX,
  RefreshCw,
  TrendingDown,
  TriangleAlert,
} from 'lucide-react';

import { kitsApi } from '../kits.api.js';
import { DEFAULT_KIT_DEMAND_FORECAST_QUERY, kitQueryKeys } from '../kits.query-keys.js';
import { KitDemandForecastTable } from './kit-demand-forecast-table.js';

export interface KitDemandForecastViewProps {
  trialId: number;
}

const HORIZON_OPTIONS = [4, 8, 12, 26, 52] as const;

function tone(active: boolean, activeCls: string, idleCls: string): string {
  return active ? activeCls : idleCls;
}

export function KitDemandForecastView({ trialId }: KitDemandForecastViewProps): JSX.Element {
  const [horizonWeeks, setHorizonWeeks] = useState<number>(
    DEFAULT_KIT_DEMAND_FORECAST_QUERY.horizonWeeks,
  );
  const [selectedLabName, setSelectedLabName] = useState<string>('all');

  const query = {
    trialId,
    page: DEFAULT_KIT_DEMAND_FORECAST_QUERY.page,
    limit: DEFAULT_KIT_DEMAND_FORECAST_QUERY.limit,
    horizonWeeks,
  };

  const forecastQuery = useQuery({
    queryKey: kitQueryKeys.demandForecast.byTrial(query),
    queryFn: () => kitsApi.getDemandForecast(query),
    enabled: trialId > 0,
    staleTime: 0,
  });

  const items = forecastQuery.data?.items ?? [];

  const labNames = useMemo(
    () => Array.from(new Set(items.map((item) => item.laboratoryName))),
    [items],
  );

  const filteredItems = useMemo(
    () =>
      selectedLabName === 'all'
        ? items
        : items.filter((item) => item.laboratoryName === selectedLabName),
    [items, selectedLabName],
  );

  const kitsWithShortage = filteredItems.filter((item) => item.hasShortage).length;
  const totalShortage = filteredItems.reduce((acc, item) => acc + item.shortageKits, 0);
  const nearestShortageDays = filteredItems.reduce<number | null>((acc, item) => {
    if (item.daysUntilShortage === null) {
      return acc;
    }
    return acc === null ? item.daysUntilShortage : Math.min(acc, item.daysUntilShortage);
  }, null);
  const expiringSoonCount = filteredItems.reduce(
    (acc, item) => acc + item.expiringSoonBatches.filter((batch) => !batch.isExpired).length,
    0,
  );

  const hasShortageTone = kitsWithShortage > 0;
  const hasTotalShortageTone = totalShortage > 0;
  const hasNearestTone = nearestShortageDays !== null;
  const hasExpiringTone = expiringSoonCount > 0;

  return (
    <div className="space-y-6" id="kit-demand-forecast-view">
      {/* Header & Filters */}
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-800 pb-5">
        <div>
          <h1 className="text-xl font-bold text-slate-100 flex items-center gap-2.5">
            <TrendingDown className="h-6 w-6 text-cyan-400" />
            <span>Prognoza zapotrzebowania i ostrzeżenia o brakach</span>
          </h1>
          <p className="text-xs text-slate-400 mt-1">
            Wirtualne wydanie FEFO pokrywa przyszłe wizyty w horyzoncie planowania i ostrzega o
            brakach zestawów (REQ-KIT-027, REQ-KIT-028, REQ-KIT-029). Moduł wyłącznie ostrzega — nie
            tworzy zamówień (Q-KIT-008).
          </p>
        </div>

        <div className="flex items-center gap-3">
          {labNames.length > 1 && (
            <select
              id="forecast-lab-filter"
              aria-label="Filtr laboratorium"
              value={selectedLabName}
              onChange={(event) => setSelectedLabName(event.target.value)}
              className="rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-200 focus:border-cyan-500 focus:outline-none"
            >
              <option value="all">Wszystkie laboratoria</option>
              {labNames.map((labName) => (
                <option key={labName} value={labName}>
                  {labName}
                </option>
              ))}
            </select>
          )}

          <label htmlFor="forecast-horizon-select" className="text-xs font-medium text-slate-400">
            Horyzont
          </label>
          <select
            id="forecast-horizon-select"
            aria-label="Horyzont planowania"
            value={horizonWeeks}
            onChange={(event) => setHorizonWeeks(Number(event.target.value))}
            className="rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-200 focus:border-cyan-500 focus:outline-none"
          >
            {HORIZON_OPTIONS.map((weeks) => (
              <option key={weeks} value={weeks}>
                {weeks} tyg.
              </option>
            ))}
          </select>

          <button
            type="button"
            id="recalculate-forecast-button"
            onClick={() => void forecastQuery.refetch()}
            disabled={forecastQuery.isFetching}
            className="inline-flex items-center gap-2 rounded-md border border-cyan-700/70 bg-cyan-950/40 px-3 py-2 text-sm font-medium text-cyan-300 hover:bg-cyan-950/70 disabled:opacity-50"
          >
            <RefreshCw className={'h-4 w-4 ' + (forecastQuery.isFetching ? 'animate-spin' : '')} />
            <span>Przelicz</span>
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div
          className={
            'flex items-center gap-4 rounded-xl border p-4 shadow-sm ' +
            tone(
              hasShortageTone,
              'border-rose-900/80 bg-rose-950/30 text-rose-200',
              'border-slate-800 bg-slate-900/60 text-slate-400',
            )
          }
        >
          <div
            className={
              'flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border ' +
              tone(
                hasShortageTone,
                'border-rose-800/80 bg-rose-950 text-rose-400',
                'border-slate-700/50 bg-slate-800/50 text-slate-500',
              )
            }
          >
            <PackageX className="h-5 w-5" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide">Zestawy z brakiem</p>
            <p className="text-2xl font-bold text-slate-100">
              {kitsWithShortage}{' '}
              <span className="text-sm font-normal text-slate-400">typów</span>
            </p>
          </div>
        </div>

        <div
          className={
            'flex items-center gap-4 rounded-xl border p-4 shadow-sm ' +
            tone(
              hasTotalShortageTone,
              'border-rose-900/80 bg-rose-950/30 text-rose-200',
              'border-slate-800 bg-slate-900/60 text-slate-400',
            )
          }
        >
          <div
            className={
              'flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border ' +
              tone(
                hasTotalShortageTone,
                'border-rose-800/80 bg-rose-950 text-rose-400',
                'border-slate-700/50 bg-slate-800/50 text-slate-500',
              )
            }
          >
            <AlertTriangle className="h-5 w-5" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide">Łączny brak</p>
            <p className="text-2xl font-bold text-slate-100">
              {totalShortage}{' '}
              <span className="text-sm font-normal text-slate-400">szt.</span>
            </p>
          </div>
        </div>

        <div
          className={
            'flex items-center gap-4 rounded-xl border p-4 shadow-sm ' +
            tone(
              hasNearestTone,
              'border-amber-900/80 bg-amber-950/30 text-amber-200',
              'border-slate-800 bg-slate-900/60 text-slate-400',
            )
          }
        >
          <div
            className={
              'flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border ' +
              tone(
                hasNearestTone,
                'border-amber-800/80 bg-amber-950 text-amber-400',
                'border-slate-700/50 bg-slate-800/50 text-slate-500',
              )
            }
          >
            <CalendarClock className="h-5 w-5" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide">Najbliższy brak</p>
            <p className="text-2xl font-bold text-slate-100" data-testid="forecast-nearest-shortage">
              {nearestShortageDays === null ? '—' : 'za ' + nearestShortageDays + ' dni'}
            </p>
          </div>
        </div>

        <div
          className={
            'flex items-center gap-4 rounded-xl border p-4 shadow-sm ' +
            tone(
              hasExpiringTone,
              'border-amber-900/80 bg-amber-950/30 text-amber-200',
              'border-slate-800 bg-slate-900/60 text-slate-400',
            )
          }
        >
          <div
            className={
              'flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border ' +
              tone(
                hasExpiringTone,
                'border-amber-800/80 bg-amber-950 text-amber-400',
                'border-slate-700/50 bg-slate-800/50 text-slate-500',
              )
            }
          >
            <TriangleAlert className="h-5 w-5" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide">Wygasają w 30 dni</p>
            <p className="text-2xl font-bold text-slate-100">
              {expiringSoonCount}{' '}
              <span className="text-sm font-normal text-slate-400">partii</span>
            </p>
          </div>
        </div>
      </div>

      {/* Forecast table */}
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-sm font-semibold text-slate-200">Zestawy wg ryzyka braku</h2>
          <span className="text-[11px] text-slate-500">
            Wirtualne FEFO · partie przeterminowane wykluczone · wizyty zarejestrowane pominięte
          </span>
        </div>

        <KitDemandForecastTable items={filteredItems} isLoading={forecastQuery.isLoading} />
      </div>
    </div>
  );
}